// Every field the editor's subject/title inputs save to is limited to 255
// characters by the API (FE-1491): subject, pre_header, mail_subject, the page
// titles, and the survey name (256).
export const TITLE_MAX_LENGTH = 255

// Counts characters the way the API (Python) does: by code point. An emoji is
// one character there, but two UTF-16 units in `String.length`, which is also
// what an input's own `maxLength` counts.
export const countCharacters = (text) => [...(text || '')].length

export const fitsLimit = (text, max = TITLE_MAX_LENGTH) =>
  countCharacters(text) <= max

/**
 * Applies an edit of an input's value only as far as `max` allows, like the
 * browser does for `maxLength`: the inserted text is cut to fit, and the text
 * around it is kept. `previous` is the value before the edit, `next` after it.
 *
 * Also returns where the cursor belongs, after the kept part of the inserted
 * text (in UTF-16 units, for `setSelectionRange`), or null when the edit fits.
 * React moves the cursor to the end when it writes a cut value back.
 */
export const limitEditWithCursor = (previous, next, max = TITLE_MAX_LENGTH) => {
  if (fitsLimit(next, max)) {
    return { value: next, cursor: null }
  }

  const before = [...(previous || '')]
  const after = [...next]

  let start = 0
  while (
    start < before.length &&
    start < after.length &&
    before[start] === after[start]
  ) {
    start++
  }

  let end = 0
  while (
    end < before.length - start &&
    end < after.length - start &&
    before[before.length - 1 - end] === after[after.length - 1 - end]
  ) {
    end++
  }

  const inserted = after.slice(start, after.length - end)
  const room = Math.max(0, max - start - end)
  const head = [...after.slice(0, start), ...inserted.slice(0, room)].join('')

  return {
    value: head + after.slice(after.length - end).join(''),
    cursor: head.length,
  }
}

// Puts the cursor back after React has written a cut value into `input`.
export const restoreCursor = (input, cursor) => {
  if (cursor === null) {
    return
  }
  requestAnimationFrame(() => {
    if (document.activeElement === input) {
      input.setSelectionRange(cursor, cursor)
    }
  })
}
